import { useProjectParams } from '@hooks/useProjectParams';
import { ConfirmationDialog } from 'components/ConfirmationDialog';
import type {
    ITableLikeTreeCheckConfirmation,
    ITableLikeTreeFilterItem,
    ITableLikeTreeItem,
    ITableLikeTreeItemNotification,
    ITableLikeTreeMenuOperationContext,
} from 'components/TableLikeTree/context';
import type { ITableLikeTreeFilterConfig } from 'components/TableLikeTree/TableLikeTree';
import { TableLikeTree } from 'components/TableLikeTree/TableLikeTree';
import { RequestStatus } from 'features/request/requestSlice';
import type { BimWorkOrderObjectSelectionData, ProjectData } from 'gql/graphql';
import {
    BimContractObjectStatusEnum,
    BimWorkOrderFormationStageEnum,
    ProjectMethodologyEnum,
    WorkOrderStatusEnum,
} from 'gql/graphql';
import { useAccess } from 'hooks/useAccess';
import { useMethodsConfirmationDialog } from 'hooks/useMethodsConfirmationDialog';
import { useProjectData } from 'hooks/useProjectData';
import { useSendGpnAnalyticsEvent } from 'hooks/useSendGpnAnalyticsEvent';
import { useContractConstructionTypes } from 'pages/Project/Common/Contract/hooks/useContractConstructionTypes';
import { ProjectPageLayout } from 'pages/Project/Common/Layouts/ProjectPageLayout';
import type { TConstructionTypesIndexes } from 'pages/Project/Common/types';
import { Badge, Button, cnMixSpace, IconAlert, Layout, Loader, Switch } from 'uikit';
import { isNumber } from 'utils';

import cn from 'classnames';
import * as R from 'ramda';
import type React from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { BimWorkOrderObjectEdit } from '../BimWorkOrderObjectEdit';
import { BimWorkOrderSubObjectEdit } from '../BimWorkOrderSubObjectEdit';
import { useGetBimWorkOrderDataFromProject } from '../hooks';
import { BimWorkOrderStructureOperation } from '../types';

import {
    useBimCompleteStructureFormation,
    useBimExcludeAllObjectsFromWorkOrder,
    useBimExcludeContractObjectToWorkOrder,
    useBimIncludeAllObjectsToWorkOrder,
    useBimIncludeContractObjectToWorkOrder,
    useBimIncludeWorkOrderSeason,
    useBimRemoveWorkOrderObject,
    useBimUpdateWorkOrderSeasons,
    useBimWorkOrderContractSeasonList,
    useBimWorkOrderObjectSelectionList,
    useBimWorkOrderSeasonRange,
    useIncludedCountByConstructionType,
} from './hooks';

import styles from './BimWorkOrderStructure.module.css';

const getParentProp = (item: BimWorkOrderObjectSelectionData) => (item.parentId ? String(item.parentId) : 'root');

export const groupByParentId = R.groupBy(getParentProp);
export const sortByParentBaseOrder = R.sortBy(R.prop('order'));

const createObjectConfirmMessage =
    'Создание объекта приведет к обновлению структуры договора. Вы уверены, что хотите продолжить?';
const deleteObjectConfirmMessage = 'Вы уверены, что хотите удалить объект? Все дочерние элементы будут удалены';
const deleteSubObjectConfirmMessage = 'Вы уверены, что хотите удалить подобъект?';

const deleteTitle = 'Удалить';
const doNotDeleteTitle = 'Не удалять';

export const confirmOperationMessages = {
    [BimWorkOrderStructureOperation.CreateObject]: {
        textConfirmation: createObjectConfirmMessage,
        textHeader: 'Создание объекта',
        cancelButtonText: 'Отменить',
        confirmButtonText: 'Продолжить',
    },
    [BimWorkOrderStructureOperation.DeleteSubObject]: {
        textConfirmation: deleteSubObjectConfirmMessage,
        textHeader: 'Удаление подобъекта',
        cancelButtonText: doNotDeleteTitle,
        confirmButtonText: deleteTitle,
    },
    [BimWorkOrderStructureOperation.DeleteObject]: {
        textConfirmation: deleteObjectConfirmMessage,
        textHeader: 'Удаление объекта',
        cancelButtonText: doNotDeleteTitle,
        confirmButtonText: deleteTitle,
    },
};

export const BimWorkOrderStructure = () => {
    const navigate = useNavigate();
    const { id: projectId, methodology, orderId } = useProjectParams();
    const workOrderId = useMemo(() => (orderId ? Number(orderId) : undefined), [orderId]);
    const { projectData, projectDataStatus, setProjectData } = useProjectData(projectId);
    const currentFrameWorkOrder = useMemo(
        () => projectData?.contractNavigation?.data?.workOrderNavs?.find((nav) => nav.id === workOrderId),
        [projectData?.contractNavigation?.data?.workOrderNavs, workOrderId],
    );

    const { sendGpnAnalyticsEvent } = useSendGpnAnalyticsEvent();

    const { orderBim: orderBimRules } = useAccess();

    const { isFrameContract, workOrderStatus } = useGetBimWorkOrderDataFromProject({});

    const [constructionTypes] = useContractConstructionTypes(projectData?.contractId, ProjectMethodologyEnum.Bim);

    const [isOpenCreateOrEditObject, setIsOpenCreateOrEditObject] = useState(false);
    const [isOpenCreateOrEditSubObject, setIsOpenCreateOrEditSubObject] = useState(false);
    const [selectedSeasonsIds] = useState<number[]>([]);
    const [checkedIds, setCheckedIds] = useState<number[]>([]);
    const [showOnlyChecked, setShowOnlyChecked] = useState(false);
    const [workOrderStructureStyle, setWorkOrderStructureStyle] = useState<React.CSSProperties>();
    const [constructionTypeId, setConstructionTypeId] = useState<number>();
    const [objectToAppendTo, setObjectToAppendTo] = useState<BimWorkOrderObjectSelectionData>();
    const [objectToEdit, setObjectToEdit] = useState<BimWorkOrderObjectSelectionData>();
    const [operationToConfirm, setOperationToConfirm] = useState<BimWorkOrderStructureOperation>();

    const { textConfirmation, textHeader, confirmButtonText, cancelButtonText } = useMemo(
        () => confirmOperationMessages[operationToConfirm as BimWorkOrderStructureOperation] || {},
        [operationToConfirm],
    );

    const isObjectsFormationStage = useMemo(
        () => currentFrameWorkOrder?.metadata.formationStage === BimWorkOrderFormationStageEnum.ObjectsFormation,
        [currentFrameWorkOrder?.metadata],
    );

    const workOrderMessageTitle = useMemo(
        () => (isFrameContract ? 'Наряд-Заказа' : 'Расчета по договору'),
        [isFrameContract],
    );

    const title = useMemo(
        () =>
            isObjectsFormationStage
                ? `Формирование структуры ${workOrderMessageTitle}`
                : `Редактирование структуры ${workOrderMessageTitle}`,
        [isObjectsFormationStage, workOrderMessageTitle],
    );

    const saveButtonTitle = useMemo(
        () => (isObjectsFormationStage ? 'Завершить формирование' : 'Завершить редактирование'),
        [isObjectsFormationStage],
    );

    const firstRowInformerMessage = useMemo(
        () =>
            isObjectsFormationStage
                ? `Выберите объекты для включения объекта и вида строительства в расчет ${workOrderMessageTitle}`
                : undefined,
        [isObjectsFormationStage, workOrderMessageTitle],
    );

    const contractId = projectData?.contractId;

    const { includedCount, setIncludedCount } = useIncludedCountByConstructionType({
        workOrderId,
    });

    const {
        bimWorkOrderSeasonSelectionReset,
        bimWorkOrderContractSeasonListData,
        bimWorkOrderContractSeasonListStatus,
    } = useBimWorkOrderContractSeasonList({ contractId });

    const { bimWorkOrderSeasonRangeData, bimWorkOrderSeasonRangeUpdate } = useBimWorkOrderSeasonRange({ workOrderId });

    const {
        bimWorkOrderObjectSelectionListReset,
        bimWorkOrderObjectSelectionListData,
        bimWorkOrderObjectSelectionListStatus,
        bimWorkOrderObjectSelectionListMakeRequest,
    } = useBimWorkOrderObjectSelectionList();

    const {
        bimIncludeContractObjectToWorkOrderReset,
        bimIncludeContractObjectToWorkOrderData,
        bimIncludeContractObjectToWorkOrderStatus,
        bimIncludeContractObjectToWorkOrderMakeRequest,
    } = useBimIncludeContractObjectToWorkOrder(workOrderId);

    const {
        bimExcludeContractObjectFromWorkOrderReset,
        bimExcludeContractObjectFromWorkOrderData,
        bimExcludeContractObjectFromWorkOrderStatus,
        bimExcludeContractObjectFromWorkOrderMakeRequest,
    } = useBimExcludeContractObjectToWorkOrder(workOrderId);

    const { bimIncludeWorkOrderSeasonStatus } = useBimIncludeWorkOrderSeason(workOrderId);

    const { updateSeasonsHandler, updateSeasonsValidationMessages, updateWorkOrderSeasonsStatus } =
        useBimUpdateWorkOrderSeasons({
            workOrderId,
            updateSeasonRange: bimWorkOrderSeasonRangeUpdate,
        });

    const {
        bimCompleteStructureFormationReset,
        bimCompleteStructureFormationData,
        bimCompleteStructureFormationStatus,
        bimCompleteStructureFormationMakeRequest,
    } = useBimCompleteStructureFormation(workOrderId);

    const updateIncludedCount = useCallback(
        (includeLen: number, isReplace = false) => {
            const key = String(constructionTypeId);

            setIncludedCount(
                (prevIncludedCount) =>
                    ({
                        ...prevIncludedCount,

                        [key]: isReplace
                            ? includeLen
                            : (prevIncludedCount?.[key as TConstructionTypesIndexes] ?? 0) + includeLen,
                    }) as any,
            );
        },
        [constructionTypeId, setIncludedCount],
    );

    const setAllCheckedIdsByCurrentType = useCallback(() => {
        const idsToCheck =
            bimWorkOrderObjectSelectionListData?.bimWorkOrderObjectSelectionList?.items
                ?.filter((i) => i.constructionType.id === constructionTypeId)
                .map((i) => i.id) || [];

        setCheckedIds(idsToCheck);
        updateIncludedCount(idsToCheck.length, true);
    }, [
        bimWorkOrderObjectSelectionListData?.bimWorkOrderObjectSelectionList?.items,
        constructionTypeId,
        updateIncludedCount,
    ]);

    const { bimIncludeAllObjectsToWorkOrderStatus, bimIncludeAllObjectsToWorkOrderMakeRequest } =
        useBimIncludeAllObjectsToWorkOrder(workOrderId, setAllCheckedIdsByCurrentType);

    const resetCheckedIds = useCallback(() => {
        setCheckedIds([]);
        updateIncludedCount(0, true);
    }, [updateIncludedCount]);

    const { bimExcludeAllObjectsFromWorkOrderStatus, bimExcludeAllObjectsFromWorkOrderMakeRequest } =
        useBimExcludeAllObjectsFromWorkOrder(workOrderId, resetCheckedIds);

    const onDeleteObjectSuccess = useCallback(() => {
        setObjectToEdit(undefined);
        setOperationToConfirm(undefined);

        if (workOrderId) {
            bimWorkOrderObjectSelectionListMakeRequest(workOrderId, constructionTypeId);
        }
    }, [bimWorkOrderObjectSelectionListMakeRequest, constructionTypeId, workOrderId]);

    const { bimRemoveWorkOrderObjectMakeRequest } = useBimRemoveWorkOrderObject(
        operationToConfirm,
        onDeleteObjectSuccess,
    );

    const tableRef = useRef<HTMLDivElement>(null);

    const informers: ITableLikeTreeItemNotification[] = useMemo(
        () =>
            bimWorkOrderObjectSelectionListData?.bimWorkOrderObjectSelectionList?.items?.reduce(
                (acc: ITableLikeTreeItemNotification[], item) => {
                    const excludeItem = updateSeasonsValidationMessages.find(
                        (i) => i?.metadata?.contractObjectId === item.id && checkedIds.includes(item.id),
                    );

                    const items = [
                        ...(excludeItem
                            ? [
                                  {
                                      message: `${excludeItem.message}${
                                          excludeItem.message && !excludeItem.message.endsWith('.') ? '.' : ''
                                      } Скорректируйте распределение и повторно удалите сезон.`,
                                      icon: <IconAlert size="xs" view="alert" />,
                                  },
                              ]
                            : []),
                        ...(item.status === BimContractObjectStatusEnum.NewRecord
                            ? [{ icon: <Badge minified size="s" status="success" /> }]
                            : []),
                        ...(item.hasPrices
                            ? [
                                  {
                                      message: item.parentId
                                          ? 'В состав подобъекта загружены сметы'
                                          : 'В состав объекта загружены сметы',
                                      icon: <Badge size="s" status="system" label="C" />,
                                  },
                              ]
                            : []),
                    ];

                    if (items.length) {
                        acc.push({
                            items,
                            treeItemId: item.id,
                        });
                    }

                    return acc;
                },
                [],
            ) || [],
        [
            bimWorkOrderObjectSelectionListData?.bimWorkOrderObjectSelectionList?.items,
            checkedIds,
            updateSeasonsValidationMessages,
        ],
    );

    const checkConfirmations: ITableLikeTreeCheckConfirmation[] = useMemo(
        () =>
            bimWorkOrderObjectSelectionListData?.bimWorkOrderObjectSelectionList?.items
                ?.filter((i) => i.hasPrices)
                ?.map((i) => ({
                    treeItemId: i.id,
                    onUncheck: {
                        message: `В составе данного ${
                            i.parentId ? 'подобъекта' : 'объекта'
                        } загружены сметы. Вы уверены, что хотите исключить ${
                            i.parentId ? 'подобъект' : 'объект'
                        } из ${workOrderMessageTitle}?`,
                        confirmLabel: 'Исключить',
                    },
                })) || [],
        [bimWorkOrderObjectSelectionListData?.bimWorkOrderObjectSelectionList?.items, workOrderMessageTitle],
    );

    const dragOperationDisabledItemsIds = useMemo(
        () =>
            bimWorkOrderObjectSelectionListData?.bimWorkOrderObjectSelectionList?.items
                ?.filter((i) => !i.workOrderObjectId)
                .map((i) => i.id),
        [bimWorkOrderObjectSelectionListData?.bimWorkOrderObjectSelectionList?.items],
    );

    const isCompletePerforming = useMemo(
        () => bimCompleteStructureFormationStatus === RequestStatus.FETCHING,
        [bimCompleteStructureFormationStatus],
    );

    const areCheckboxesDisabled = useMemo(
        () =>
            !isFrameContract ||
            bimIncludeContractObjectToWorkOrderStatus === RequestStatus.FETCHING ||
            bimExcludeContractObjectFromWorkOrderStatus === RequestStatus.FETCHING ||
            bimIncludeAllObjectsToWorkOrderStatus === RequestStatus.FETCHING ||
            bimExcludeAllObjectsFromWorkOrderStatus === RequestStatus.FETCHING,
        [
            bimExcludeAllObjectsFromWorkOrderStatus,
            bimExcludeContractObjectFromWorkOrderStatus,
            bimIncludeAllObjectsToWorkOrderStatus,
            bimIncludeContractObjectToWorkOrderStatus,
            isFrameContract,
        ],
    );

    const tableTreeItems: ITableLikeTreeItem[] = useMemo(() => {
        if (bimWorkOrderObjectSelectionListData?.bimWorkOrderObjectSelectionList?.items?.length) {
            const groupedByParent = groupByParentId(
                bimWorkOrderObjectSelectionListData?.bimWorkOrderObjectSelectionList
                    ?.items as BimWorkOrderObjectSelectionData[],
            );
            const mapper = (o: BimWorkOrderObjectSelectionData): ITableLikeTreeItem => ({
                id: o.id,
                title: o.name,
                children: sortByParentBaseOrder(groupedByParent[o.id] || [])?.map(mapper),
            });

            return sortByParentBaseOrder(groupedByParent.root || []).map(mapper);
        }

        return [];
    }, [bimWorkOrderObjectSelectionListData?.bimWorkOrderObjectSelectionList?.items]);

    const isLoadingPage = useMemo(
        () =>
            projectDataStatus === RequestStatus.FETCHING ||
            bimWorkOrderContractSeasonListStatus === RequestStatus.FETCHING ||
            (bimWorkOrderObjectSelectionListStatus === RequestStatus.FETCHING && !constructionTypeId),
        [
            bimWorkOrderObjectSelectionListStatus,
            bimWorkOrderContractSeasonListStatus,
            constructionTypeId,
            projectDataStatus,
        ],
    );

    const breadCrumbsItems = useMemo(
        () => [
            {
                label: projectData?.name,
                href: `/projects/${methodology}/${projectData?.uniqueId}`,
            },
            {
                label: 'Договор',
                href: `/projects/${methodology}/${projectData?.uniqueId}/contract`,
            },
        ],
        [methodology, projectData?.uniqueId, projectData?.name],
    );

    const topFilterConfig: ITableLikeTreeFilterConfig = useMemo(
        () => ({
            filterItems: (bimWorkOrderContractSeasonListData ?? []).map((s) => {
                const isFirstActive = bimWorkOrderSeasonRangeData?.firstSeason?.id === s?.id;
                const isLastActive = bimWorkOrderSeasonRangeData?.lastSeason?.id === s?.id;

                return { id: s?.id, title: s?.year, isFirstActive, isLastActive };
            }),
            title: 'Выбор сезонов',
            isFictiveFilter: true,
            onFilterItemCheckChange: (seasonValues) => {
                if (seasonValues) {
                    updateSeasonsHandler(
                        seasonValues as {
                            firstSeason: ITableLikeTreeFilterItem | undefined;
                            lastSeason: ITableLikeTreeFilterItem | undefined;
                        },
                    );
                }
            },
            disabled:
                (!isFrameContract &&
                    [WorkOrderStatusEnum.OnFormation, WorkOrderStatusEnum.InProgress].includes(workOrderStatus)) ||
                (isFrameContract && workOrderStatus === WorkOrderStatusEnum.InProgress) ||
                bimIncludeWorkOrderSeasonStatus === RequestStatus.FETCHING ||
                updateWorkOrderSeasonsStatus === RequestStatus.FETCHING,
        }),
        [
            bimWorkOrderContractSeasonListData,
            isFrameContract,
            workOrderStatus,
            bimIncludeWorkOrderSeasonStatus,
            updateWorkOrderSeasonsStatus,
            bimWorkOrderSeasonRangeData?.firstSeason?.id,
            bimWorkOrderSeasonRangeData?.lastSeason?.id,
            updateSeasonsHandler,
        ],
    );

    const leftFilterConfig: ITableLikeTreeFilterConfig = useMemo(
        () => ({
            filterItems: constructionTypes
                ?.map((ct) => ({ id: ct.id, title: ct.name }))
                .sort((a, b) => a.title.localeCompare(b.title)),
            isFictiveFilter: true,
            disabled: true,
            title: 'Виды строительства',
        }),
        [constructionTypes],
    );

    const disabledCheckBoxesItemsIds = useMemo(
        () =>
            workOrderStatus === WorkOrderStatusEnum.InProgress
                ? bimWorkOrderObjectSelectionListData?.bimWorkOrderObjectSelectionList?.items
                      ?.filter(
                          (i) =>
                              (i.status === BimContractObjectStatusEnum.ContractCondition &&
                                  i.isIncluded &&
                                  !i.isActKC2Object) ||
                              i.hasActsKC2,
                      )
                      ?.map((i) => i.id)
                : [],
        [bimWorkOrderObjectSelectionListData?.bimWorkOrderObjectSelectionList?.items, workOrderStatus],
    );

    const actionFn = useCallback(() => {
        if (operationToConfirm === BimWorkOrderStructureOperation.CreateObject) {
            setIsOpenCreateOrEditObject(true);
        } else if (objectToEdit) {
            const obj = bimWorkOrderObjectSelectionListData?.bimWorkOrderObjectSelectionList?.items?.find(
                (o) => o.id === objectToEdit.id,
            );

            if (obj && workOrderId) {
                bimRemoveWorkOrderObjectMakeRequest(workOrderId, obj.id);
            }
        }
    }, [
        bimRemoveWorkOrderObjectMakeRequest,
        bimWorkOrderObjectSelectionListData?.bimWorkOrderObjectSelectionList?.items,
        objectToEdit,
        operationToConfirm,
        workOrderId,
    ]);

    const { isOpenConfirmationDialog, handleOpenConfirmationDialog, handleCloseConfirmationDialog, handleActionQuery } =
        useMethodsConfirmationDialog({
            actionFn,
        });

    const onOpenCreateSubObject = useCallback(
        (itemId?: number, context?: ITableLikeTreeMenuOperationContext) => {
            const item = context?.item;

            const obj = bimWorkOrderObjectSelectionListData?.bimWorkOrderObjectSelectionList?.items?.find(
                (o) => o.id === item?.id,
            );

            setObjectToAppendTo(obj as BimWorkOrderObjectSelectionData);

            setIsOpenCreateOrEditSubObject(true);
        },
        [bimWorkOrderObjectSelectionListData?.bimWorkOrderObjectSelectionList?.items],
    );

    const onOpenEditObject = useCallback(
        (itemId?: number, context?: ITableLikeTreeMenuOperationContext) => {
            const item = context?.item;

            const obj = bimWorkOrderObjectSelectionListData?.bimWorkOrderObjectSelectionList?.items?.find(
                (o) => o.id === item?.id,
            );

            setObjectToEdit(obj as BimWorkOrderObjectSelectionData);

            if (item?.parent) {
                const parent = bimWorkOrderObjectSelectionListData?.bimWorkOrderObjectSelectionList?.items?.find(
                    (o) => o.id === item?.parent?.id,
                );

                setObjectToAppendTo(parent as BimWorkOrderObjectSelectionData);
                setIsOpenCreateOrEditSubObject(true);
            } else {
                setIsOpenCreateOrEditObject(true);
            }
        },
        [bimWorkOrderObjectSelectionListData?.bimWorkOrderObjectSelectionList?.items],
    );

    const onDeleteObject = useCallback(
        (itemId?: number, context?: ITableLikeTreeMenuOperationContext) => {
            const item = context?.item;

            setOperationToConfirm(
                item?.parent
                    ? BimWorkOrderStructureOperation.DeleteSubObject
                    : BimWorkOrderStructureOperation.DeleteObject,
            );

            const obj = bimWorkOrderObjectSelectionListData?.bimWorkOrderObjectSelectionList?.items?.find(
                (o) => o.id === item?.id,
            );

            setObjectToEdit(obj as BimWorkOrderObjectSelectionData);

            handleOpenConfirmationDialog();
        },
        [bimWorkOrderObjectSelectionListData?.bimWorkOrderObjectSelectionList?.items, handleOpenConfirmationDialog],
    );

    const menuItemsGetter = useCallback(
        (treeItem: ITableLikeTreeItem) => {
            const treeItemData = bimWorkOrderObjectSelectionListData?.bimWorkOrderObjectSelectionList?.items?.find(
                (o) => o.id === treeItem.id,
            );

            const isEditDisabled =
                (workOrderStatus === WorkOrderStatusEnum.InProgress &&
                    treeItemData?.status === BimContractObjectStatusEnum.ContractCondition) ||
                treeItemData?.hasActsKC2 ||
                !orderBimRules.canEditStructure;
            const isDeleteDisabled =
                !treeItemData?.fromWorkOrder || treeItemData?.hasActsKC2 || !orderBimRules.canEditStructure;
            const isCreateSubObjectDisabled = treeItemData?.hasPrices || !orderBimRules.canEditStructure;

            return [
                {
                    label: 'Редактировать',
                    action: onOpenEditObject,
                    disabled: isEditDisabled,
                    itemId: 1,
                    groupId: 1,
                },
                {
                    label: 'Создать новый подобъект',
                    itemId: 2,
                    groupId: 1,
                    action: onOpenCreateSubObject,
                    disabled: isCreateSubObjectDisabled,
                },
                {
                    label: 'Удалить',
                    action: onDeleteObject,
                    disabled: isDeleteDisabled,
                    itemId: 3,
                    groupId: 2,
                },
            ];
        },
        [
            bimWorkOrderObjectSelectionListData?.bimWorkOrderObjectSelectionList?.items,
            onDeleteObject,
            onOpenCreateSubObject,
            onOpenEditObject,
            orderBimRules.canEditStructure,
            workOrderStatus,
        ],
    );

    const onItemCheckChange = useCallback(
        (item: ITableLikeTreeItem, isChecked: boolean) => {
            if (isChecked) {
                bimIncludeContractObjectToWorkOrderMakeRequest(item.id);
            } else {
                bimExcludeContractObjectFromWorkOrderMakeRequest(item.id);
            }
        },
        [bimExcludeContractObjectFromWorkOrderMakeRequest, bimIncludeContractObjectToWorkOrderMakeRequest],
    );

    const onOpenCreateObject = useCallback(() => {
        setOperationToConfirm(BimWorkOrderStructureOperation.CreateObject);

        handleOpenConfirmationDialog();
    }, [handleOpenConfirmationDialog]);

    const onCreateObjectClose = useCallback(
        (isSuccess?: boolean) => {
            setIsOpenCreateOrEditObject(false);
            setOperationToConfirm(undefined);
            setObjectToEdit(undefined);

            if (isSuccess && workOrderId && constructionTypeId) {
                bimWorkOrderObjectSelectionListMakeRequest(workOrderId, constructionTypeId);
                updateIncludedCount(1);
            }
        },
        [bimWorkOrderObjectSelectionListMakeRequest, constructionTypeId, updateIncludedCount, workOrderId],
    );

    const onCreateOrEditSubObjectClose = useCallback(
        (isSuccess?: boolean, newSubObjectId?: number) => {
            if (isSuccess && workOrderId) {
                bimWorkOrderObjectSelectionListMakeRequest(workOrderId, constructionTypeId);

                if (newSubObjectId) {
                    bimIncludeContractObjectToWorkOrderMakeRequest(newSubObjectId);
                }
            }

            setIsOpenCreateOrEditSubObject(false);
            setObjectToAppendTo(undefined);
            setObjectToEdit(undefined);
        },
        [
            bimIncludeContractObjectToWorkOrderMakeRequest,
            bimWorkOrderObjectSelectionListMakeRequest,
            constructionTypeId,
            workOrderId,
        ],
    );

    const onCompleteClick = useCallback(() => {
        if (isObjectsFormationStage) {
            bimCompleteStructureFormationMakeRequest();

            sendGpnAnalyticsEvent({
                componentId: 'rim_get to order_pressed',
                projectId,
                contractId: projectData?.contractId,
                orderId: workOrderId,
                orderStatus: workOrderStatus,
                methodology: ProjectMethodologyEnum.Bim,
                projectName: projectData?.name,
                projectType: projectData?.contractType,
                projectDO: projectData?.subsidiary?.name,
            });
        } else {
            navigate(`/projects/bim/${projectId}/order/${workOrderId}`);
        }
    }, [
        bimCompleteStructureFormationMakeRequest,
        isObjectsFormationStage,
        navigate,
        projectData?.contractId,
        projectData?.contractType,
        projectData?.name,
        projectData?.subsidiary?.name,
        projectId,
        sendGpnAnalyticsEvent,
        workOrderId,
        workOrderStatus,
    ]);

    const onShowOnlyCheckedChange = useCallback(({ checked }) => {
        setShowOnlyChecked(checked);
    }, []);

    const onLeftFilterRowSelected = useCallback((itemId: number) => {
        setConstructionTypeId(itemId);
    }, []);

    const onCheckAll = useCallback(
        (isChecked: boolean) => {
            if (!constructionTypeId) {
                return;
            }

            if (isChecked) {
                bimIncludeAllObjectsToWorkOrderMakeRequest(constructionTypeId);
            } else {
                bimExcludeAllObjectsFromWorkOrderMakeRequest(constructionTypeId);
            }
        },
        [bimExcludeAllObjectsFromWorkOrderMakeRequest, bimIncludeAllObjectsToWorkOrderMakeRequest, constructionTypeId],
    );

    useEffect(() => {
        if (workOrderId && constructionTypeId) {
            bimWorkOrderObjectSelectionListMakeRequest(workOrderId, constructionTypeId);
        }
    }, [bimWorkOrderObjectSelectionListMakeRequest, constructionTypeId, workOrderId]);

    useEffect(() => {
        bimCompleteStructureFormationReset(undefined);
        bimWorkOrderSeasonSelectionReset(undefined);
        bimWorkOrderObjectSelectionListReset(undefined);
        bimIncludeContractObjectToWorkOrderReset(undefined);
        bimExcludeContractObjectFromWorkOrderReset(undefined);

        return () => {
            bimCompleteStructureFormationReset(undefined);
            bimWorkOrderSeasonSelectionReset(undefined);
            bimWorkOrderObjectSelectionListReset(undefined);
            bimIncludeContractObjectToWorkOrderReset(undefined);
            bimExcludeContractObjectFromWorkOrderReset(undefined);
        };
    }, [
        bimCompleteStructureFormationReset,
        bimExcludeContractObjectFromWorkOrderReset,
        bimIncludeContractObjectToWorkOrderReset,
        bimWorkOrderObjectSelectionListReset,
        bimWorkOrderSeasonSelectionReset,
    ]);

    useEffect(() => {
        if (bimWorkOrderObjectSelectionListData?.bimWorkOrderObjectSelectionList?.items?.length) {
            const checkedItems = bimWorkOrderObjectSelectionListData?.bimWorkOrderObjectSelectionList?.items
                .filter((i) => i.isIncluded)
                .map((i) => i.id);

            updateIncludedCount(checkedItems.length, true);
            setCheckedIds(checkedItems);
        }
    }, [
        bimWorkOrderObjectSelectionListData?.bimWorkOrderObjectSelectionList?.items,
        constructionTypeId,
        updateIncludedCount,
    ]);

    useEffect(() => {
        if (
            bimIncludeContractObjectToWorkOrderStatus === RequestStatus.SUCCESS &&
            bimIncludeContractObjectToWorkOrderData?.bimIncludeContractObjectToWorkOrder?.result === true &&
            bimIncludeContractObjectToWorkOrderData?.bimIncludeContractObjectToWorkOrder.data?.length
        ) {
            const includeLen =
                bimIncludeContractObjectToWorkOrderData?.bimIncludeContractObjectToWorkOrder.data?.length || 0;
            const checksToInclude =
                bimIncludeContractObjectToWorkOrderData?.bimIncludeContractObjectToWorkOrder.data.filter(
                    (id) => !checkedIds.includes(id),
                );

            updateIncludedCount(includeLen);
            setCheckedIds([...checkedIds, ...checksToInclude]);
            bimIncludeContractObjectToWorkOrderReset(undefined);
        }
    }, [
        bimIncludeContractObjectToWorkOrderData?.bimIncludeContractObjectToWorkOrder.data,
        bimIncludeContractObjectToWorkOrderData?.bimIncludeContractObjectToWorkOrder?.result,
        bimIncludeContractObjectToWorkOrderReset,
        bimIncludeContractObjectToWorkOrderStatus,
        checkedIds,
        constructionTypeId,
        setIncludedCount,
        updateIncludedCount,
    ]);

    useEffect(() => {
        if (
            bimExcludeContractObjectFromWorkOrderStatus === RequestStatus.SUCCESS &&
            bimExcludeContractObjectFromWorkOrderData?.bimExcludeContractObjectFromWorkOrder?.result === true &&
            bimExcludeContractObjectFromWorkOrderData?.bimExcludeContractObjectFromWorkOrder.data?.length
        ) {
            const excludeLen =
                bimExcludeContractObjectFromWorkOrderData?.bimExcludeContractObjectFromWorkOrder.data?.length || 0;
            const checksToExclude = checkedIds.filter(
                (id) =>
                    !bimExcludeContractObjectFromWorkOrderData?.bimExcludeContractObjectFromWorkOrder.data?.includes(
                        id,
                    ),
            );

            updateIncludedCount(-excludeLen);
            setCheckedIds(checksToExclude);
            bimExcludeContractObjectFromWorkOrderReset(undefined);
        }
    }, [
        bimExcludeContractObjectFromWorkOrderData?.bimExcludeContractObjectFromWorkOrder.data,
        bimExcludeContractObjectFromWorkOrderData?.bimExcludeContractObjectFromWorkOrder?.result,
        bimExcludeContractObjectFromWorkOrderReset,
        bimExcludeContractObjectFromWorkOrderStatus,
        checkedIds,
        constructionTypeId,
        setIncludedCount,
        updateIncludedCount,
    ]);

    useEffect(() => {
        if (
            bimCompleteStructureFormationStatus === RequestStatus.SUCCESS &&
            bimCompleteStructureFormationData?.bimCompleteStructureFormation?.result === true
        ) {
            bimCompleteStructureFormationReset(undefined);

            setProjectData(undefined as unknown as ProjectData);

            navigate(
                `/projects/bim/${projectId}/order/${bimCompleteStructureFormationData?.bimCompleteStructureFormation?.data?.id}`,
                { state: { openEditRequisites: true } },
            );
        }
    }, [
        bimCompleteStructureFormationData?.bimCompleteStructureFormation?.data?.id,
        bimCompleteStructureFormationData?.bimCompleteStructureFormation?.result,
        bimCompleteStructureFormationReset,
        bimCompleteStructureFormationStatus,
        navigate,
        orderId,
        projectId,
        setProjectData,
    ]);

    useEffect(() => {
        const onResize = () => {
            if (tableRef.current) {
                setWorkOrderStructureStyle({
                    '--others-height':
                        isNumber(tableRef.current?.getBoundingClientRect?.()?.top) &&
                        `${
                            (tableRef.current?.getBoundingClientRect?.()?.top as number) +
                            (firstRowInformerMessage ? 145 : 91)
                        }px`,
                } as React.CSSProperties);
            }
        };

        window.addEventListener('resize', onResize);

        onResize();

        return () => {
            window.removeEventListener('resize', onResize);
        };
    }, [firstRowInformerMessage]);

    useEffect(() => {
        if (constructionTypes.length) {
            setConstructionTypeId(constructionTypes.sort((a, b) => a.name.localeCompare(b.name))[0].id);
        }
    }, [constructionTypes]);

    return (
        <ProjectPageLayout title={title} breadCrumbsItems={breadCrumbsItems} style={{ height: 'calc(100% - 60px)' }}>
            <Layout className={cn(styles.wrapper_toolbar, cnMixSpace({ mB: 'xl' }))}>
                <Button
                    style={{ marginRight: '10px' }}
                    label={saveButtonTitle}
                    size="s"
                    loading={isCompletePerforming}
                    onClick={onCompleteClick}
                />
            </Layout>
            <div id="workOrderStructure" style={workOrderStructureStyle}>
                {isLoadingPage ? (
                    <Loader />
                ) : (
                    <div className={styles.bimWorkOrderStructureContainer} ref={tableRef}>
                        <TableLikeTree
                            hierarchyColumnTitle=""
                            items={tableTreeItems}
                            menuItemsGetter={menuItemsGetter}
                            isRowCheckAvailable
                            showExpandAllSwitchInHierarchyHeader
                            leftFilterConfig={leftFilterConfig}
                            topFilterConfig={topFilterConfig}
                            topFilterCheckedIds={selectedSeasonsIds}
                            onItemCheckChange={onItemCheckChange}
                            itemsCheckedIds={checkedIds}
                            areCheckboxesDisabled={areCheckboxesDisabled}
                            areOuterBordersEnabled
                            tableTreeColumnLeftSide={
                                <Switch
                                    label="Показать выбранные"
                                    checked={showOnlyChecked}
                                    size="s"
                                    onChange={onShowOnlyCheckedChange}
                                    style={{ marginRight: 20 }}
                                />
                            }
                            tableTreeColumnRightSide={
                                <Button
                                    label="Создать новый объект"
                                    onClick={onOpenCreateObject}
                                    view="secondary"
                                    size="s"
                                    style={{ marginLeft: 'auto' }}
                                    disabled={!orderBimRules.canEditStructure}
                                />
                            }
                            informerMessage={firstRowInformerMessage}
                            showCheckedOnly={showOnlyChecked}
                            onLeftFilterRowSelected={onLeftFilterRowSelected}
                            leftFilterSelectedId={constructionTypeId}
                            informers={informers}
                            checkConfirmations={checkConfirmations}
                            onCheckAll={onCheckAll}
                            disabledCheckBoxesItemsIds={disabledCheckBoxesItemsIds}
                            dragOperationDisabledItemsIds={dragOperationDisabledItemsIds}
                            isLeftFilterMenuColumnVisible={false}
                            showCheckedItemsCount
                            isDragOperationDisabled
                            isDragButtonHidden
                            leftFilterCheckedCountCache={includedCount}
                        />
                    </div>
                )}
            </div>
            {isOpenCreateOrEditObject && constructionTypeId && (
                <BimWorkOrderObjectEdit
                    workOrderId={workOrderId}
                    contractId={contractId}
                    constructionTypeId={constructionTypeId}
                    onClose={onCreateObjectClose}
                    seasons={bimWorkOrderContractSeasonListData}
                    objectToEdit={objectToEdit}
                />
            )}
            {isOpenCreateOrEditSubObject && (
                <BimWorkOrderSubObjectEdit
                    workOrderId={workOrderId}
                    onClose={onCreateOrEditSubObjectClose}
                    objectToEdit={objectToEdit}
                    parentObjectToAppend={objectToAppendTo}
                />
            )}
            <ConfirmationDialog
                textHeader={textHeader}
                textConfirmation={textConfirmation}
                cancelButtonText={cancelButtonText}
                confirmButtonText={confirmButtonText}
                isOpened={isOpenConfirmationDialog}
                onSubmit={handleActionQuery}
                onClose={handleCloseConfirmationDialog}
            />
        </ProjectPageLayout>
    );
};

BimWorkOrderStructure.displayName = 'BimWorkOrderStructure';
